// Shared nav config for the employee portal shell (sidebar + mobile drawer).
// Groups mirror the Phase-1 decluttered EmployeeDashboard sections so the
// persistent shell and the dashboard landing page stay in sync.
//
// Icons are FontAwesome sharp-solid class names (matching the existing portal
// convention, e.g. EmployeeDashboard). The admin "Setup & Data" group is gated
// on userProfile?.role === 'admin' by the consumer.

export const PORTAL_HOME = {
	label: 'Dashboard',
	icon: 'fa-gauge-high',
	to: '/portal/work',
	end: true, // exact-match so it isn't highlighted for every /portal/work/* route
};

export const PORTAL_NAV_GROUPS = [
	{
		title: 'Daily Operations',
		icon: 'fa-calendar-day',
		items: [
			{ label: 'Today', icon: 'fa-calendar-day', to: '/portal/work/today' },
			{
				// Moved out of Notes & Planning 2026-08-02 (Anthony): Tasks is the
				// canonical work queue and was sitting eleventh in the sidebar, below
				// four groups it is opened more often than. It is a daily-driver
				// surface, so it belongs in Daily Operations rather than beside the
				// writing surfaces it merely shares a noun with.
				//
				// Second position, NOT between Students and Billing: those two are
				// asserted adjacent (Billing === Students + 1) because billing is
				// admin/owner-only and its placement was specifically asked for.
				// Inserting here keeps that invariant intact.
				//
				// Deliberately NOT duplicated — the card offered "move or duplicate",
				// and a route appearing twice in the sidebar would also appear twice
				// in the dashboard card picker, which derives from this file.
				label: 'Tasks',
				icon: 'fa-clipboard-check',
				to: '/portal/work/tasks',
			},
			{ label: 'Students', icon: 'fa-users', to: '/portal/work/students' },
			{
				// Invoices, payments and family balances (autopay slice 2).
				//
				// `adminOnly` is a PER-ITEM flag here, sitting inside a group every
				// teacher can see — unlike Setup & Data, which is gated as a whole.
				// It must match the billing rules exactly (isAdmin() || isOwner()),
				// because "teachers do NOT see billing" is a real wall in
				// firestore.rules and on every callable, not a nav preference.
				// Annelise (employee, owner:false) is the person this hides it from.
				label: 'Billing',
				icon: 'fa-file-invoice-dollar',
				to: '/portal/work/billing',
				adminOnly: true,
			},
		],
	},
	{
		title: 'Scheduling',
		icon: 'fa-calendar-week',
		items: [
			{
				// FIRST in the group on purpose: this is the one scheduling surface
				// a person opens about THEMSELVES, and the two below it are studio
				// tools. Added 2026-08-30 — TeacherSchedule was mounted only inside
				// TeacherDashboard, which WorkShell renders for non-admin employees
				// only, so the two admins (both linked to real `teachers` docs) had
				// no route to their own assignments at all.
				//
				// NOT placed in Daily Operations beside Today, which would read as
				// its natural home: portalNav.test.js pins that group to an exact
				// array to protect the Tasks/Billing ordering, and widening a
				// deliberate pin to land a nav item is the wrong trade.
				//
				// Ungated — every teacher should see their own schedule. Annelise
				// reaches the same component through her dashboard tab and has no
				// sidebar today, so this is additive for her rather than duplicate.
				label: 'My Schedule',
				icon: 'fa-user-clock',
				to: '/portal/work/my-schedule',
			},
			{
				// adminOnly, matching the route gate added 2026-08-30. A teacher
				// reaching this board could not change anything (every action is
				// isAdmin-gated) but could read every teacher's load and lesson
				// CAP from the rail. Per-item rather than a whole-group gate,
				// like Billing above: the other two Scheduling entries are
				// genuinely for everyone.
				label: 'Teacher Scheduling',
				icon: 'fa-chalkboard-user',
				to: '/portal/work/teacher-scheduling',
				adminOnly: true,
			},
			{
				// adminOnly: every teacher's monthly count, which is pay data
				// (card dQzkKYcUNzuvhO0DEDES, 2026-09-23). Beside the rotation
				// board because the count is stamped FROM the rotation.
				label: 'Lessons Taught',
				icon: 'fa-clipboard-check',
				to: '/portal/work/lessons-taught',
				adminOnly: true,
			},
			{
				// New unified scheduling surface — built in Step C. Wired here now per
				// the Phase-2 handoff; resolves once /scheduling lands in the same PR.
				label: 'Scheduling',
				icon: 'fa-calendar-week',
				to: '/portal/work/scheduling',
			},
			{
				label: 'Manage Lessons',
				icon: 'fa-calendar-plus',
				to: '/portal/work/manage-lessons',
			},
		],
	},
	{
		title: 'Registrations & Sales',
		icon: 'fa-guitar',
		items: [
			{
				label: 'Workshop Enrollments',
				icon: 'fa-clipboard-user',
				to: '/portal/work/workshop-enrollments',
			},
			{
				label: 'Lesson Pack Purchases',
				icon: 'fa-music',
				to: '/portal/work/lesson-packs',
			},
			{
				label: 'Inquiries',
				icon: 'fa-user-plus',
				to: '/portal/work/inquiries',
			},
		],
	},
	{
		title: 'Communications',
		icon: 'fa-bullhorn',
		items: [
			{
				label: 'Email Announcements',
				icon: 'fa-bullhorn',
				to: '/portal/work/announcements',
			},
			{
				// E4 Layer 1. Shown to admins/owners always; to a non-admin
				// employee only when permissions.sendEmail is granted. `requiresPerm`
				// is enforced in PortalLayout via hasPerm() (admins/owners bypass).
				label: 'Email Families',
				icon: 'fa-paper-plane',
				to: '/portal/work/email-families',
				requiresPerm: 'sendEmail',
			},
			{
				label: 'Email Templates',
				icon: 'fa-envelope',
				to: '/portal/work/email-templates',
			},
			{
				label: 'Portal Announcements',
				icon: 'fa-megaphone',
				to: '/portal/work/manage-announcements',
			},
		],
	},
	{
		title: 'Notes & Planning',
		icon: 'fa-file-pen',
		items: [
			// Docs — the in-portal Notion replacement (workDocs). Distinct from
			// Lesson Recaps below, which browses the `documents` collection.
			{ label: 'Docs', icon: 'fa-file-lines', to: '/portal/work/docs' },
			// Renamed from "Notes" 2026-07-24: this surface only ever listed Lesson
			// Recap records, and "Notes" collided with both Docs and Song Notes —
			// exactly the ambiguity the three-name vocabulary rule exists to kill.
			{ label: 'Lesson Recaps', icon: 'fa-file-pen', to: '/portal/work/notes' },
			// Tasks used to sit here (Phase C pointed it at the Work module). Moved
			// to Daily Operations 2026-08-02 — see the note on that entry.
			// Two recital tools, deliberately (decision 4, 2026-08-06): Planning owns
			// the program + rehearsals off the canonical song registry; Recital Songs
			// stays suggestion intake and song prep (keys, karaoke, lyrics).
			{
				label: 'Recital Planning',
				icon: 'fa-list-ol',
				to: '/portal/work/recital-planning',
			},
			{
				label: 'Recital Songs',
				icon: 'fa-microphone-stand',
				to: '/portal/work/recital-songs',
			},
			{
				label: 'Material Requests',
				icon: 'fa-clipboard-list',
				to: '/portal/work/requests',
			},
			{
				// The warmup catalog — 69 pedagogy exercises, browsable and
				// playable, and the surface Grace reviews them on.
				//
				// "Notes & Planning" rather than Daily Operations: this is
				// reference material a teacher opens while preparing, not a
				// daily-driver queue — and portalNav.test.js pins Daily
				// Operations to an exact array to protect the Tasks/Billing
				// ordering, so widening that pin to land a nav item would be
				// the wrong trade (the same reasoning that put My Schedule in
				// Scheduling rather than beside Today).
				//
				// UNGATED. Every teacher should see it; the Firestore rules
				// gate warmupExercises on isEmployeeOrAdmin() with no
				// capability key, so the nav and the database agree. Note this
				// only reaches ADMINS in practice — WorkShell renders the
				// sidebar for role 'admin' alone, so Annelise gets here through
				// her dashboard tab instead (Work/TeacherDashboard).
				label: 'Warmups',
				icon: 'fa-music',
				to: '/portal/work/warmups',
			},
		],
	},
	{
		// The staff guide (2026-10-30): how-tos plus a guided tour on the real
		// page. Its own group, last, so it is easy to find and touches none of
		// the pinned orderings above. Ungated: it reads no data. Like Warmups,
		// this reaches admins only (WorkShell renders the sidebar for role
		// 'admin'); a teacher opens it as the Help tab on her Dashboard.
		title: 'Help',
		icon: 'fa-circle-question',
		items: [
			{ label: 'How do I…', icon: 'fa-signs-post', to: '/portal/work/help' },
		],
	},
];

// Admin-only. Rendered last and de-emphasized. The legacy generate-time-slots /
// manage-time-slots pages are intentionally NOT listed here — they are absorbed
// into the unified Scheduling surface (Step C). Create Test
// Lesson stays a dashboard action (not duplicated into the shell).
export const PORTAL_ADMIN_GROUP = {
	title: 'Setup & Data',
	icon: 'fa-screwdriver-wrench',
	adminOnly: true,
	note: 'One-time setup and data tools. Admin only.',
	items: [
		{ label: 'Import Students', icon: 'fa-upload', to: '/portal/work/import' },
		{
			label: 'Import Lessons',
			icon: 'fa-file-import',
			to: '/portal/work/import-lessons',
		},
		{
			label: 'Link Lessons',
			icon: 'fa-link',
			to: '/portal/work/link-lessons',
		},
		{
			label: 'Setup Student Types',
			icon: 'fa-user-tag',
			to: '/portal/work/setup-student-types',
		},
		{
			label: 'Admin Panel',
			icon: 'fa-shield-halved',
			to: '/portal/work/admin',
		},
		{
			// ownerOnly, NOT adminOnly — canon carries pay, worker classification
			// and family billing detail, which core.md's workspace-separation rule
			// keeps in the Owners teamspace. firestore.rules gates canonDocs on
			// isOwner() alone, and this must match or the nav offers a page that
			// then refuses to load.
			label: 'Canon',
			icon: 'fa-book',
			to: '/portal/work/canon',
			ownerOnly: true,
		},
	],
};

/**
 * The nav groups a given viewer may actually see.
 *
 * Pure and exported so the gating can be TESTED. It used to live inline in
 * PortalLayout, where the only way to exercise it was to render the whole
 * shell with auth, theme, router and Web Awesome mocked — which meant in
 * practice it was never exercised at all. "Teachers do NOT see billing" is a
 * real wall in firestore.rules and on every billing callable; the nav is the
 * cosmetic half of it, and a cosmetic half that silently breaks is how someone
 * discovers a page they should not have been offered.
 *
 * Three filters, in order:
 *   1. Setup & Data is appended only for admins (GROUP-level gate, unchanged).
 *   2. `item.adminOnly` drops an item for anyone who is neither admin nor
 *      owner — the shape Billing needs, since it sits inside Daily Operations,
 *      a group every teacher sees.
 *   3. `item.ownerOnly` drops an item for anyone who is not an OWNER — strictly
 *      narrower than adminOnly, and the difference is real rather than
 *      theoretical: `anthony+testadmin@` is admin with owner:false, so the two
 *      flags disagree on a live account today. Canon needs this one, because
 *      firestore.rules gates canonDocs on isOwner() alone and a nav entry that
 *      offers a page the database then refuses is worse than no entry.
 *   4. `item.requiresPerm` drops capability-gated items (admins/owners
 *      short-circuit inside hasPerm).
 * Then any group left empty disappears rather than rendering a bare heading.
 *
 * ADMIN **OR** OWNER on the adminOnly check, and the `||` is load-bearing:
 * firestore.rules grants billing to `isAdmin() || isOwner()` and keeps those
 * deliberately separate, because owner status is independent of role. Grace is
 * both today, so they agree — but the rules comments explicitly anticipate her
 * role changing, and an isAdmin-only nav gate would then hide Billing from
 * someone the database still admits.
 */
export function visibleNavGroups(userProfile, hasPerm) {
	const isAdmin = userProfile?.role === 'admin';
	const isOwner = userProfile?.owner === true;
	const isAdminOrOwner = isAdmin || isOwner;
	const base = isAdmin
		? [...PORTAL_NAV_GROUPS, PORTAL_ADMIN_GROUP]
		: PORTAL_NAV_GROUPS;
	return base
		.map((group) => ({
			...group,
			items: group.items.filter(
				(item) =>
					(!item.adminOnly || isAdminOrOwner) &&
					(!item.ownerOnly || isOwner) &&
					(!item.requiresPerm || hasPerm(userProfile, item.requiresPerm))
			),
		}))
		.filter((group) => group.items.length > 0);
}
